import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, Smartphone, ThumbsUp, ShieldCheck, Tag, Bot, MemoryStick, HardDrive, Wifi, Download, Cpu, Layers, Sparkles, Package } from "lucide-react";
import AppHero from "../components/AppHero";
import FaqAccordion from "../components/FaqAccordion";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { GameCard } from "../components/GameGrid";
import { getGameDetail, getGameSeo } from "../mock";
import { useData } from "../data/DataContext";
import Seo from "../components/Seo";

const GameDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getGameBySlug, allGames, gameDownloadUrl, loaded } = useData();
  const game = getGameBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!game) {
    if (!loaded) return <div className="py-32 text-center text-emerald-50/60 font-bold">Loading...</div>;
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-extrabold text-white">Game not found</h1>
        <button onClick={() => navigate("/")} className="mt-6 bg-[#ffb224] text-[#062a13] font-bold px-6 py-3 rounded-xl">
          Back to All Games
        </button>
      </div>
    );
  }

  const detail = getGameDetail(game);
  const seo = getGameSeo(game);
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: `${game.name} APK`,
        operatingSystem: "Android",
        applicationCategory: "GameApplication",
        image: `${window.location.origin}${game.logo}`,
        description: seo.description,
        offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: detail.rating, ratingCount: parseInt(detail.downloads) * 10 },
      },
      {
        "@type": "FAQPage",
        mainEntity: detail.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
          { "@type": "ListItem", position: 2, name: game.name, item: window.location.href },
        ],
      },
    ],
  };
  const related = allGames.filter((gm) => gm.slug !== game.slug).slice(0, 10);

  const DOWNLOAD_URL = gameDownloadUrl(game);
  const handleDownload = () => window.open(DOWNLOAD_URL, "_blank", "noopener");

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Seo title={seo.title} description={seo.description} keywords={seo.keywords} image={game.logo} type="article" schema={schema} />
      <AppHero
        name={game.name}
        logo={game.logo}
        rating={detail.rating}
        size={detail.size}
        downloads={detail.downloads}
        bonus={game.bonus}
        minWithdraw={game.minWithdraw}
        onDownload={handleDownload}
        testPrefix="game-detail"
        tagline={seo.title}
      />

      <section className="mt-8">
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { id: "glance", icon: Star, title: `${game.name} at a Glance`, body: detail.glance },
            { id: "safety", icon: ShieldCheck, title: `Playing Safely on ${game.name}`, body: detail.safety },
            { id: "verdict", icon: ThumbsUp, title: `Should You Try ${game.name}?`, body: detail.verdict },
          ].map(({ id, icon: Icon, title, body }) => (
            <AccordionItem key={id} value={id} data-testid={`game-detail-acc-${id}`} className="bg-[#073c1b] border-2 border-[#ffb224]/60 rounded-xl px-4 shadow-sm">
              <AccordionTrigger className="font-bold text-[#ffb224] text-[15px] sm:text-lg hover:no-underline py-3.5 [&>svg]:text-[#ffb224]">
                <span className="flex items-center gap-2.5 text-left"><Icon size={20} strokeWidth={2.4} className="shrink-0" />{title}</span>
              </AccordionTrigger>
              <AccordionContent className="text-emerald-50/80 text-sm sm:text-[15px] leading-relaxed pb-4">{body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <h2 className="text-xl sm:text-2xl font-extrabold text-white mt-8 flex items-center gap-3">
          <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#ffb224] text-[#062a13] flex items-center justify-center border-b-[3px] border-[#b47400] shrink-0"><Download size={18} strokeWidth={2.6} /></span>
          How to Download {game.name} APK
        </h2>
        <p className="text-emerald-50/70 text-[15px] sm:text-base leading-relaxed mt-1">{detail.downloadIntro}</p>
        <ol className="mt-3 space-y-2">
          {detail.steps.map((step, i) => (
            <li key={step} className="flex items-start gap-3 bg-[#073c1b] border border-[#1c5f35] rounded-xl px-3 py-2.5">
              <span className="w-7 h-7 rounded-lg bg-[#ffb224] text-[#062a13] text-sm font-extrabold flex items-center justify-center shrink-0 border-b-2 border-[#b47400]">{i + 1}</span>
              <span className="text-emerald-50/80 text-[14px] sm:text-[15px] leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>

        <h2 className="text-xl sm:text-2xl font-extrabold text-white mt-8 mb-3 flex items-center gap-3">
          <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#ffb224] text-[#062a13] flex items-center justify-center border-b-[3px] border-[#b47400] shrink-0"><Cpu size={18} strokeWidth={2.6} /></span>
          {game.name} System Requirements
        </h2>
        <div className="bg-[#073c1b] border border-[#1c5f35] rounded-xl overflow-hidden shadow-sm">
          {detail.requirements.map(([k, v], i) => {
            const Icon = [Bot, MemoryStick, HardDrive, Wifi, Smartphone, Package][i];
            return (
            <div key={k} className={`flex items-center justify-between gap-3 px-4 py-3.5 text-[15px] sm:text-base ${i % 2 === 0 ? "bg-[#0a4a22]" : "bg-[#073c1b]"} ${i ? "border-t border-[#1c5f35]" : ""}`}>
              <span className="flex items-center gap-2.5 font-bold text-white"><Icon size={18} className="text-[#ffb224] shrink-0" strokeWidth={2.4} />{k}</span>
              <span className="text-emerald-50/80 font-medium text-right">{v}</span>
            </div>
            );
          })}
        </div>
        <p className="text-emerald-50/70 text-[15px] leading-relaxed mt-3">{detail.reqNote}</p>

        <div className="mt-7 w-full bg-gradient-to-b from-[#ffc14d] to-[#ffb224] text-[#062a13] font-extrabold text-lg py-3.5 rounded-xl border-b-[5px] border-[#b47400] text-center">
          <span className="inline-flex items-center justify-center gap-2"><Layers size={20} strokeWidth={2.6} /> Other Related Apps <Sparkles size={18} /></span>
        </div>

        <div data-testid="game-detail-related-list" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          {related.map((rel, i) => (
            <GameCard key={rel.slug} game={rel} rank={i + 1} downloadUrl={gameDownloadUrl(rel)} />
          ))}
        </div>

        <FaqAccordion title={`Common Questions about ${game.name}`} faqs={detail.faqs} />

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
          <Tag size={14} className="text-emerald-50/50" />
          {seo.keywords.slice(0, 10).map((t) => (
            <span key={t} className="text-xs font-semibold bg-[#15562e] text-emerald-50/80 px-3 py-1.5 rounded-full">{t}</span>
          ))}
        </div>
      </section>
    </div>
  );
};

export default GameDetail;
